import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Bot, User, Send, Sparkles, MessageCircle, Zap, Award, ThumbsUp, Copy } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useToast } from '@/hooks/use-toast';

interface Message {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: Date;
}

export function ChatPanel() {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'ai',
      content: "Hello! I'm your AI assistant. How can I help you?",
      timestamp: new Date()
    }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  // Suggested prompts for quick start
  const suggestedPrompts = [
    "Create me a 3-tier program where silver holders earn 1 point per $5, and gold holders earn 1.5 points per $5, with points expiring in 12 months",
    "Design a cashback program with 2% rewards for groceries and 1% for everything else",
    "Set up a referral program that gives 500 bonus points for each successful referral",
    "Create a birthday rewards program that gives members a special discount on their birthday month",
    "Design a VIP program with exclusive access to premium rewards and early sale notifications",
    "Build a seasonal promotion that doubles points during holiday shopping periods",
    "Create a milestone program that rewards members for reaching spending thresholds"
  ];

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollElement = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    }
  }, [messages]);

  const handleSendMessage = async () => {
    if (!message.trim()) {
      toast({
        title: "Empty message",
        description: "Please enter a message to send.",
        variant: "destructive"
      });
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: message,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setMessage('');
    setIsLoading(true);
    setShowLoader(true);

    try {
      // Simulate AI processing with loader
      await new Promise(resolve => setTimeout(resolve, 2200));
      setShowLoader(false);
      // Generate AI response based on user input
      const aiResponse = generateAIResponse(message);
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: aiResponse,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
      setShowLoader(false);
    }
  };

  const handleSampleQuestion = (question: string) => {
    setMessage(question);
  };

  const generateAIResponse = (userInput: string): string => {
    const input = userInput.toLowerCase();
    if (input.includes('tier') || input.includes('level')) {
      return `Great idea! I'll help you create a tiered loyalty program. Based on your request, here's what I recommend:\n\n**Program Structure:**\n• **Silver Tier**: 1 point per $5 spent (20% earning rate)\n• **Gold Tier**: 1.5 points per $5 spent (30% earning rate)\n• **Platinum Tier**: 2 points per $5 spent (40% earning rate)\n\n**Key Features:**\n• Points expire after 12 months to encourage engagement\n• Tier advancement based on annual spending or points earned\n• Exclusive perks for each tier (early access, bonus multipliers, etc.)\n\n**Recommended Tier Thresholds:**\n• Silver: $0-$999 annual spend\n• Gold: $1,000-$4,999 annual spend  \n• Platinum: $5,000+ annual spend\n\nWould you like me to help you set up the redemption catalog or configure any specific rewards for each tier?`;
    }
    if (input.includes('cashback') || input.includes('cash back')) {
      return `Perfect! Let's design a cashback program for you:\n\n**Cashback Structure:**\n• **Groceries**: 2% cashback on all grocery purchases\n• **Gas Stations**: 1.5% cashback on fuel purchases  \n• **Everything Else**: 1% cashback on all other purchases\n\n**Program Features:**\n• Monthly cashback statements\n• Minimum redemption threshold of $25\n• Cashback never expires\n• Bonus categories that rotate quarterly\n\n**Additional Recommendations:**\n• Consider adding bonus cashback rates for online shopping (2.5%)\n• Implement a referral bonus system\n• Add seasonal promotions with elevated rates\n\nWould you like me to help you set up the merchant categories or configure the redemption process?`;
    }
    if (input.includes('referral')) {
      return `Excellent choice! Referral programs are highly effective. Here's my recommendation:\n\n**Referral Program Structure:**\n• **Referrer Reward**: 500 bonus points for each successful referral\n• **Referee Reward**: 250 welcome bonus points after first purchase\n• **Tier Bonuses**: Higher tiers get additional referral multipliers\n\n**Key Features:**\n• Unlimited referrals allowed\n• 30-day window for referee to make qualifying purchase\n• Social sharing integration for easy referrals\n• Tracking dashboard for referral status\n\n**Bonus Structure:**\n• Silver: Standard 500 points\n• Gold: 750 points per referral\n• Platinum: 1,000 points per referral\n\nWould you like me to help you set up the referral tracking system or design the sharing mechanics?`;
    }
    if (input.includes('birthday') || input.includes('special')) {
      return `What a thoughtful program idea! Here's my birthday rewards recommendation:\n\n**Birthday Program Features:**\n• **Birthday Month Bonus**: 2x points on all purchases during birthday month\n• **Special Birthday Gift**: 1,000 bonus points on member's birthday\n• **Exclusive Birthday Discount**: 15% off one purchase during birthday month\n• **Extended Offer**: Birthday rewards valid for full month, not just one day\n\n**Enhanced Features:**\n• Birthday surprise delivery for VIP members\n• Early access to birthday month sales\n• Personalized birthday offers based on purchase history\n• Family birthday tracking for household accounts\n\n**Communication Strategy:**\n• Birthday month welcome email\n• Mid-month reminder if not used\n• Week-before-expiry notification\n\nWould you like me to help you set up the birthday tracking system or design personalized offer mechanics?`;
    }
    // Default response for general queries
    return `I understand you're looking to create something special! Based on your request, I can help you design a comprehensive program solution.\n\n**What I can help you create:**\n• Multi-tier loyalty programs with custom earning rates\n• Cashback and rewards structures\n• Referral and social sharing programs\n• Seasonal and promotional campaigns\n• VIP and exclusive access programs\n• Points expiration and engagement strategies\n\n**Next Steps:**\nTo give you the most relevant recommendations, could you tell me more about:\n• Your target audience or member base\n• Preferred reward types (points, cashback, perks)\n• Budget considerations or program goals\n• Any specific features you'd like to include\n\nFeel free to ask me about any specific aspect of program creation, and I'll provide detailed guidance tailored to your needs!`;
  };

  const handleSuggestedPrompt = (prompt: string) => {
    setMessage(prompt);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex flex-1 gap-6 h-full">
        {/* Main chat area */}
        <div className="flex flex-col flex-1 h-full">
          <div className="flex-1 flex flex-col p-0 min-h-0">
            {/* Messages Area */}
            <ScrollArea className="flex-1 p-6 min-h-0" ref={scrollAreaRef}>
              <div className="space-y-4 pr-4">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex gap-3 ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {msg.type === 'ai' && (
                      <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                        <Bot className="h-4 w-4 text-white" />
                      </div>
                    )}
                    <div className={`max-w-[80%] ${msg.type === 'user' ? 'order-first' : ''}`}>
                      <div
                        className={`rounded-lg px-4 py-3 ${
                          msg.type === 'user'
                            ? 'bg-blue-500 text-white ml-auto'
                            : 'bg-gray-100 text-gray-900'
                        }`}
                      >
                        <div className="whitespace-pre-wrap text-xs leading-relaxed">{msg.content}</div>
                        
                      </div>
                      <div className={`text-xs text-gray-500 mt-1 ${msg.type === 'user' ? 'text-right' : 'text-left'}`}>
                        {msg.type === 'ai' && (
                          <div className="flex gap-2 mt-2">
                            <button
                              className="p-1 rounded hover:bg-gray-200 transition"
                              title="Like"
                              type="button"
                            >
                              <ThumbsUp className="w-3 h-3 text-gray-500" />
                            </button>
                            <button
                              className="p-1 rounded hover:bg-gray-200 transition"
                              title="Copy"
                              type="button"
                              onClick={() => navigator.clipboard.writeText(msg.content)}
                            >
                              <Copy className="w-3 h-3 text-gray-500" />
                            </button>
                          </div>
                        )}

                        {msg.type === 'user' && (
                          <div className={`text-xs text-gray-500 mt-1 text-right'}`}>{msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                        )}
                        
                      </div>
                      
                    </div>
                    {msg.type === 'user' && (
                      <div className="h-8 w-8 rounded-full bg-gray-500 flex items-center justify-center flex-shrink-0">
                        <User className="h-4 w-4 text-white" />
                      </div>
                    )}
                  </div>
                ))}
                {showLoader && (
                  <div className="flex gap-3 justify-start">
                    <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
                      <Bot className="h-4 w-4 text-white" />
                    </div>
                    <div className="bg-gray-100 rounded-lg px-4 py-3 flex items-center min-h-[40px]">
                      <ThreeDotsLoader />
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
            {/* Sample Questions */}
            <div className="border-t p-3 bg-gray-50">
              <div className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                <Sparkles className="h-3 w-3" />
                Quick Questions:
              </div>
              <div className="flex flex-wrap gap-1">
                {["Identify trends in member engagement", 
                "Recommend ways to increase program participation",
                "What rewards are most popular in my programs?"
                ].slice(0, 3).map((question, index) => (
                  <Button 
                    key={index}
                    variant="outline" 
                    size="sm"
                    className="text-xs h-auto py-1 px-2"
                    onClick={() => handleSampleQuestion(question)}
                  >
                    {question}
                  </Button>
                ))}
              </div>
            </div>
            {/* Sticky Message Input */}
            <div className="border-t p-4 bg-white sticky bottom-0 z-10">
              <div className="flex gap-3">
                <Textarea
                  placeholder="Describe the program you want to create..."
                  className="flex-1 min-h-[60px] max-h-[120px] resize-none text-sm"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isLoading}
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={isLoading || !message.trim()}
                  className="px-6"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                <span>Press Enter to send, Shift+Enter for new line</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ThreeDotsLoader() {
  return (
    <div className="flex items-center gap-1">
      <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></span>
      <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></span>
      <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></span>
    </div>
  );
} 